import React, { useContext, useState } from 'react';
import styled from 'astroturf/react';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { trackClick } from 'js/utils/trackClick';
import {
  CiterightContext,
  CiterightContextProvider,
} from './CiterightContextProvider';
import { PreprintUploadStep } from './PreprintUploadStep';
import { ReferenceReportPage } from './ReferenceReportPage';
import { PreprintSuggestionsStep } from './PreprintSuggestionsStep';
import { PreprintExportStep } from './PreprintExportStep';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CiterightPreprintReportProps {
  publicToken?: string;
  showStep2?: boolean;
}

// ─── Step config ─────────────────────────────────────────────────────────────

const STEPS = [
  { number: 1, label: 'Upload' },
  { number: 2, label: 'Review reference issues' },
  { number: 3, label: 'Review reference suggestions' },
  { number: 4, label: 'Export' },
] as const;

type StepNumber = (typeof STEPS)[number]['number'];

// ─── Styled components ───────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  background: var(--background-white, #ffffff);
  padding-bottom: 72px; /* room for sticky footer */
`;

const HeaderSection = styled.div`
  background: var(--background-white, #ffffff);
  border-bottom: 1px solid var(--stroke-light);
  padding: 20px 24px 0;
  text-align: center;

  @media (max-width: 567px) {
    padding-top: 36px;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const StepperRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  padding: 16px 24px 24px;
  max-width: 640px;
  margin: 0 auto;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
`;

const StepConnectorBase = styled.div`
  position: absolute;
  top: 12px;
  left: calc(50% + 12px);
  right: calc(-50% + 12px);
  height: 2px;
  z-index: 0;
`;

const StepCircleBase = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  z-index: 1;
  border: 2px solid transparent;
`;

const StepLabelBase = styled.div`
  font-size: 11px;
  text-align: center;
  margin-top: 4px;
  max-width: 80px;
  line-height: 1.3;
`;

const ContentArea = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const StickyFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--background-beige-dark);
  border-top: 1px solid var(--stroke-light);
  padding: 12px 0;
  z-index: 100;
`;

const StickyFooterInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FooterStepLabel = styled.div`
  @media (max-width: 567px) {
    display: none;
  }
`;

// ─── Stepper ─────────────────────────────────────────────────────────────────

interface StepConfig {
  readonly number: StepNumber;
  readonly label: string;
}

interface PreprintStepperProps {
  visibleSteps: StepConfig[];
  currentStep: StepNumber;
  onStepClick: (step: StepNumber) => void;
}

const BLUE = 'var(--brand-colors-blue-600)';
const GREEN = 'var(--brand-colors-success-500)';
const GRAY_LINE = 'var(--stroke-light)';
const GRAY_CIRCLE_BORDER = 'var(--brand-colors-neutral-400)';
const TEXT_SECONDARY = 'var(--text-secondary)';

const PreprintStepper: React.FC<PreprintStepperProps> = ({
  visibleSteps,
  currentStep,
  onStepClick,
}) => (
  <StepperRow>
    {visibleSteps.map((step, idx) => {
      const displayNumber = idx + 1;
      const isCompleted = step.number < currentStep;
      const isActive = step.number === currentStep;
      const isLast = idx === visibleSteps.length - 1;
      const circleStyle: React.CSSProperties = isCompleted
        ? { background: GREEN, borderColor: GREEN, color: '#fff' }
        : isActive
          ? { background: BLUE, borderColor: BLUE, color: '#fff' }
          : {
              background: '#fff',
              borderColor: GRAY_CIRCLE_BORDER,
              color: TEXT_SECONDARY,
            };

      const handleClick = () => {
        onStepClick(step.number);
        if (step.number === 1) {
          window.location.href = '/citeright';
        }
      };

      return (
        <StepItem
          key={step.number}
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          {!isLast && <StepConnectorBase style={{ background: GRAY_LINE }} />}
          <StepCircleBase style={circleStyle}>
            {isCompleted ? (
              <Icon name="check" size={14} />
            ) : (
              <span>{displayNumber}</span>
            )}
          </StepCircleBase>
          <StepLabelBase
            style={{
              color: isActive ? BLUE : TEXT_SECONDARY,
              fontWeight: isActive ? 600 : undefined,
            }}
          >
            {step.label}
          </StepLabelBase>
        </StepItem>
      );
    })}
  </StepperRow>
);

// ─── Inner component (has access to context) ─────────────────────────────────

interface PreprintReportInnerProps {
  showStep2: boolean;
}

const PreprintReportInner: React.FC<PreprintReportInnerProps> = ({
  showStep2,
}) => {
  const visibleSteps = showStep2
    ? [...STEPS]
    : STEPS.filter((s) => s.number !== 2);
  const [currentStep, setCurrentStep] = useState<StepNumber>(showStep2 ? 2 : 3);
  const context = useContext(CiterightContext);

  const currentStepDisplayIndex =
    visibleSteps.findIndex((s) => s.number === currentStep) + 1;

  const getPrevStep = (step: StepNumber): StepNumber | null => {
    const idx = visibleSteps.findIndex((s) => s.number === step);
    return idx > 0 ? visibleSteps[idx - 1].number : null;
  };

  const getNextStep = (step: StepNumber): StepNumber | null => {
    const idx = visibleSteps.findIndex((s) => s.number === step);
    return idx < visibleSteps.length - 1 ? visibleSteps[idx + 1].number : null;
  };

  const goToPrev = () => {
    const prevStep = getPrevStep(currentStep);
    if (prevStep !== null) {
      trackClick({
        source: 'citeright.preprint.nav.prev',
        entityId: context?.reportId ?? undefined,
        extra: { from_step: currentStep, to_step: prevStep },
      });
      setCurrentStep(prevStep);
    }
  };

  const goToNext = () => {
    if (currentStep === 3 && context?.savedReferences.length === 0) {
      trackClick({
        source: 'citeright.preprint.no_refs_alert',
        entityId: context?.reportId ?? undefined,
      });
      if (
        !window.confirm(
          "You haven't saved any references yet. Are you sure you want to continue?",
        )
      ) {
        return;
      }
    }
    const nextStep = getNextStep(currentStep);
    if (nextStep !== null) {
      trackClick({
        source: 'citeright.preprint.nav.next',
        entityId: context?.reportId ?? undefined,
        extra: { from_step: currentStep, to_step: nextStep },
      });
      setCurrentStep(nextStep);
    }
  };

  return (
    <PageWrapper>
      <HeaderSection>
        <Heading size="sm" as="h1">
          {context?.report?.preprint_title &&
          context?.report?.preprint_article_link ? (
            <>
              {'Report for '}
              {/* eslint-disable-next-line no-restricted-syntax */}
              <a
                href={context.report.preprint_article_link}
                target="_blank"
                rel="noreferrer"
                className="ds2-5-heading-link"
                style={{ color: 'var(--text-link)' }}
              >
                {context.report.preprint_title.length > 80
                  ? context.report.preprint_title
                      .slice(0, 80)
                      .replace(/\s+\S*$/, '') + '\u2026'
                  : context.report.preprint_title}{' '}
                <Icon name="open_in_new" size={18} />
              </a>
            </>
          ) : (
            (context?.report?.preprint_title ?? 'Report for your preprint')
          )}
        </Heading>
        <Body
          size="sm"
          style={{ color: 'var(--text-secondary)', marginTop: '4px' }}
        >
          Catch citation problems before reviewers do.{' '}
          <BodyLink
            href="/citeright"
            size="sm"
            external
            target="_blank"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              color: 'var(--brand-colors-blue-600)',
            }}
          >
            Learn more about CiteRight
            <Icon name="open_in_new" size={12} />
          </BodyLink>
        </Body>
        <PreprintStepper
          visibleSteps={visibleSteps}
          currentStep={currentStep}
          onStepClick={(step) => {
            trackClick({
              source: 'citeright.preprint.nav.stepper',
              entityId: context?.reportId ?? undefined,
              extra: { from_step: currentStep, to_step: step },
            });
            setCurrentStep(step);
          }}
        />
      </HeaderSection>

      <ContentArea>
        {currentStep === 1 && (
          <ImpressionWatcher
            source="citeright.preprint.step1.view"
            entity_id={context?.reportId ?? undefined}
          >
            <PreprintUploadStep
              onGoToStep2={() => setCurrentStep(showStep2 ? 2 : 3)}
            />
          </ImpressionWatcher>
        )}
        {currentStep === 2 && (
          <ImpressionWatcher
            source="citeright.preprint.step2.view"
            entity_id={context?.reportId ?? undefined}
          >
            <ReferenceReportPage />
          </ImpressionWatcher>
        )}
        {currentStep === 3 && (
          <ImpressionWatcher
            source="citeright.preprint.step3.view"
            entity_id={context?.reportId ?? undefined}
          >
            <PreprintSuggestionsStep />
          </ImpressionWatcher>
        )}
        {currentStep === 4 && (
          <ImpressionWatcher
            source="citeright.preprint.step4.view"
            entity_id={context?.reportId ?? undefined}
            extra={{
              saved_reference_count: context?.savedReferences.length ?? 0,
            }}
          >
            <PreprintExportStep onGoToStep3={() => setCurrentStep(3)} />
          </ImpressionWatcher>
        )}
      </ContentArea>

      <StickyFooter>
        <StickyFooterInner>
          <Button
            size="sm"
            secondary
            disabled={currentStep === 1}
            onClick={currentStep === 1 ? undefined : goToPrev}
          >
            ← Previous
          </Button>
          <FooterStepLabel>
            <Body size="sm" style={{ color: 'var(--text-secondary)' }}>
              Step {currentStepDisplayIndex}/{visibleSteps.length}:{' '}
              {currentStep === 4
                ? 'Export saved references'
                : visibleSteps.find((s) => s.number === currentStep)!.label}
            </Body>
          </FooterStepLabel>
          {currentStep === 4 ? (
            <ButtonLink
              key="start-new"
              size="sm"
              secondary
              href="/citeright"
              target="_blank"
              rel="noreferrer"
              iconRight="open_in_new"
              trackClick={{
                source: 'citeright.preprint.start_new',
                entityId: context?.reportId ?? undefined,
              }}
            >
              Start new report
            </ButtonLink>
          ) : (
            <Button key="next-step" size="sm" secondary onClick={goToNext}>
              Next →
            </Button>
          )}
        </StickyFooterInner>
      </StickyFooter>
    </PageWrapper>
  );
};

// ─── Root component ──────────────────────────────────────────────────────────

const CiterightPreprintReport: React.FC<CiterightPreprintReportProps> = ({
  publicToken,
  showStep2 = false,
}) => (
  <CiterightContextProvider publicToken={publicToken}>
    <PreprintReportInner showStep2={showStep2} />
  </CiterightContextProvider>
);

export default CiterightPreprintReport;
